"use client";

import { useState, useEffect, useMemo } from "react";
import debounce from "lodash.debounce";
import { fetchGeocodeMulti } from "../../services/geocodeService";
import { AddressCoordinates, Coordinates } from "@/types/distance.types";
import { showPopup } from "../warnings/Popup";

interface AddressAutocompleteProps {
  value: string;
  onSelect: (address: string, coords?: Coordinates) => void;
  placeholder?: string;
  disabled?: boolean;
}

export default function AddressAutocomplete({
  value,
  onSelect,
  placeholder = "",
  disabled = false,
}: AddressAutocompleteProps) {
  const [input, setInput] = useState(value);
  const [suggestions, setSuggestions] = useState<AddressCoordinates[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isHoveringDropdown, setIsHoveringDropdown] = useState(false);

  const fetchSuggestions = useMemo(() => 
    debounce(async (address: string) => {
      if (!address.trim()) return setSuggestions([]);
      setIsFetching(true);
      try {
        const results = await fetchGeocodeMulti(address, 3);

        if (!results) {
          setSuggestions([]);
          return null;
        };
        
        setSuggestions(results);
      } catch {
        setSuggestions([]);
        showPopup(
          "Unexpected Error",
          "An unexpected error has occured."
        )
      } finally {
        setIsFetching(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    setInput(value);

    if (!value.trim()) {
      setSuggestions([]);
      setIsFocused(false);
      fetchSuggestions.cancel?.();
    }
  }, [value, fetchSuggestions]);

  useEffect(() => {
    if (input.trim()) fetchSuggestions(input);
    else setSuggestions([]);
  }, [input, fetchSuggestions]);

  const handleSelect = (address: string, coords?: Coordinates) => {
    onSelect(address, coords);
    setInput(address);
    setSuggestions([]);
    setIsFocused(false);
  };

  // Called when user explicitly presses Enter or blurs (manual entry)
  const handleManualSelect = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    // call onSelect WITHOUT coords so the parent knows to geocode
    onSelect(trimmed);
    setSuggestions([]);
    setIsFocused(false);
  };

  const hasSuggestions =
    suggestions.some((s) => (s.address ?? "").trim() !== "");

  const shouldShowDropdown =
    isFocused && input.trim().length > 0;

  const handleBlur = () => {
    // Hide only if the mouse isn't over the dropdown
    if (!isHoveringDropdown) setIsFocused(false);
  };

  return (
    <div className="autocomplete-wrapper">
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="autocomplete-input"
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          // Only do manual select if the mouse isn't currently over the dropdown
          handleBlur();
          if (!isHoveringDropdown) {
            handleManualSelect();
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleManualSelect();
          }
          if (e.key === "Escape") {
            e.currentTarget.blur(); 
          }
        }}
      />

      {shouldShowDropdown && (
        <div
          className="autocomplete-dropdown"
          onMouseEnter={() => setIsHoveringDropdown(true)}
          onMouseLeave={() => setIsHoveringDropdown(false)}
        >
          {isFetching ? (
            <div className="autocomplete-loading">Loading...</div>
          ) : hasSuggestions ? (
            <ul>
              {suggestions.map((s, i) =>
                s.address.trim() ? (
                  // use onMouseDown to avoid blur-before-click race, then call handleSelect with coords
                  <li
                    key={i}
                    onMouseDown={(e) => {
                      // prevent the input from blurring before we handle the click
                      e.preventDefault();
                      handleSelect(s.address, s.coordinates);
                    }}
                  >
                    {s.address}
                  </li>
                ) : null
              )}
            </ul>
          ) : (
            <div className="autocomplete-no-results">No addresses found</div>
          )}
        </div>
      )}

    </div>
  );
}
